import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Collapse,
  CircularProgress,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import LaunchOutlinedIcon from "@mui/icons-material/LaunchOutlined";
import CommentsPanel from "../../components/CommentsPanel";
import ContactedStatusControl from "../../components/ContactedStatusControl";
import LeadAssignmentPanel from "../../components/LeadAssignmentPanel";
import { LEAD_CONTEXTS } from "../../helpers/leadContexts";
import {
  formatCompanyHouseCompanyType,
  formatCompanyHouseFacetValue,
  formatCompanyHouseStatus,
} from "./companyHouseDisplay";

function hasMeaningfulValue(value) {
  return (
    value !== undefined &&
    value !== null &&
    String(value).trim() !== "" &&
    String(value).trim().toLowerCase() !== "unknown"
  );
}

function formatValue(value) {
  return hasMeaningfulValue(value) ? String(value) : "Unknown";
}

function DetailTextRow({ label, value }) {
  if (!hasMeaningfulValue(value)) {
    return null;
  }

  return (
    <Typography sx={{ fontSize: "0.93rem", color: "#334155" }}>
      <strong>{label}:</strong> {value}
    </Typography>
  );
}

function getTextValue(value) {
  if (typeof value === "string") {
    return value.trim();
  }

  if (!value || typeof value !== "object") {
    return "";
  }

  return [
    value.text,
    value.headline && value.detail ? `${value.headline} — ${value.detail}` : "",
    value.headline,
    value.detail,
    value.background_summary,
    value.summary,
  ].find(hasMeaningfulValue) || "";
}

function getPersonTextValue(value) {
  if (!value || typeof value !== "object") {
    return getTextValue(value);
  }

  const name = value.name;
  const role = value.role || value.verified_role;
  const background = value.background_summary || value.text;

  return [
    [name, role].filter(hasMeaningfulValue).join(" — "),
    background,
  ].filter(hasMeaningfulValue).join(". ");
}

function formatGeneratedAt(value) {
  if (!hasMeaningfulValue(value)) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
  }).format(date);
}

function sourceLabel(source) {
  if (!source || typeof source !== "object") {
    return "";
  }

  return source.title || source.publisher || source.source_id || "";
}

function getSourceUrl(source) {
  if (typeof source === "string") {
    return source.trim();
  }

  if (!source || typeof source !== "object") {
    return "";
  }

  return source.url || source.href || source.link || "";
}

function getLinkDescriptor(link) {
  if (typeof link === "string") {
    return link.trim();
  }

  if (!link || typeof link !== "object") {
    return "";
  }

  return [
    link.label,
    link.title,
    link.name,
    link.type,
    link.publisher,
    link.source_id,
    getSourceUrl(link),
  ].filter(hasMeaningfulValue).join(" ");
}

function normalizeExternalUrl(url) {
  if (!hasMeaningfulValue(url)) {
    return "";
  }

  const trimmedUrl = String(url).trim();
  return /^https?:\/\//i.test(trimmedUrl) ? trimmedUrl : `https://${trimmedUrl}`;
}

function findLink(links, matcher) {
  return links.find((link) => {
    const url = getSourceUrl(link);
    if (!hasMeaningfulValue(url)) {
      return false;
    }

    return matcher(`${getLinkDescriptor(link)} ${url}`.toLowerCase());
  });
}

function formatMetadataValue(value) {
  if (!hasMeaningfulValue(value)) {
    return "";
  }

  return String(value)
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function EnrichmentSection({ title, items = [], accent = "#2563eb", emptyText, renderItem, maxItems = 6 }) {
  const visibleItems = items
    .map((item) => ({
      item,
      text: renderItem ? renderItem(item) : getTextValue(item),
    }))
    .filter(({ text }) => hasMeaningfulValue(text));
  const previewItems = visibleItems.slice(0, maxItems);
  const hiddenCount = visibleItems.length - previewItems.length;

  if (previewItems.length === 0 && !emptyText) {
    return null;
  }

  return (
    <Box
      sx={{
        border: "1px solid rgba(15, 23, 42, 0.1)",
        borderLeft: `4px solid ${accent}`,
        borderRadius: 2,
        backgroundColor: "#ffffff",
        p: 1.5,
        minWidth: 0,
      }}
    >
      <Typography sx={{ fontWeight: 800, color: "#0f172a", fontSize: "0.96rem", mb: 0.75 }}>
        {title}
      </Typography>
      {previewItems.length === 0 ? (
        <Typography sx={{ color: "#64748b", fontSize: "0.88rem" }}>{emptyText}</Typography>
      ) : (
        <Box component="ul" sx={{ m: 0, pl: 2.1, display: "grid", gap: 0.65 }}>
          {previewItems.map(({ item, text }, index) => (
            <Typography
              component="li"
              key={item?.item_id || item?.signal_id || item?.angle_id || item?.watchout_id || item?.gap_id || item?.name || index}
              sx={{ color: "#1f2937", fontSize: "0.91rem", lineHeight: 1.42 }}
            >
              {text}
            </Typography>
          ))}
        </Box>
      )}
      {hiddenCount > 0 && (
        <Typography sx={{ mt: 0.75, color: "#64748b", fontSize: "0.8rem", fontWeight: 700 }}>
          +{hiddenCount} more
        </Typography>
      )}
    </Box>
  );
}

function CompanyHouseEnrichmentPanel({ companyNumber, expanded, getApiJson }) {
  const [enrichment, setEnrichment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadedCompanyNumber, setLoadedCompanyNumber] = useState("");
  const [error, setError] = useState("");
  const [activeEnrichmentTab, setActiveEnrichmentTab] = useState("signals");
  const [sourcesExpanded, setSourcesExpanded] = useState(false);
  const [sourcesListMode, setSourcesListMode] = useState(false);

  useEffect(() => {
    if (!expanded || !getApiJson || !hasMeaningfulValue(companyNumber)) {
      return;
    }

    if (loadedCompanyNumber === companyNumber) {
      return;
    }

    let active = true;
    setLoading(true);
    setError("");

    getApiJson(`/api/companyhouse/enrichment/${encodeURIComponent(companyNumber)}`, {
      allowNotFound: true,
    })
      .then((payload) => {
        if (!active) {
          return;
        }
        setEnrichment(payload || null);
        setLoadedCompanyNumber(companyNumber);
      })
      .catch((requestError) => {
        if (!active) {
          return;
        }
        console.error("Unable to load Company House enrichment:", requestError);
        setError(requestError.message || "Unable to load enrichment.");
        setLoadedCompanyNumber(companyNumber);
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [companyNumber, expanded, getApiJson, loadedCompanyNumber]);

  if (!hasMeaningfulValue(companyNumber)) {
    return null;
  }

  const whyItMatters = enrichment?.why_it_matters?.text;
  const generatedAt = formatGeneratedAt(enrichment?.generated_at);
  const status = enrichment?.processing?.status;
  const confidence = formatMetadataValue(enrichment?.confidence);
  const officialWebsite = enrichment?.company?.official_website;
  const officialLinks = Array.isArray(enrichment?.company?.official_links)
    ? enrichment.company.official_links
    : [];
  const sources = Array.isArray(enrichment?.sources) ? enrichment.sources : [];
  const externalLinks = [...officialLinks, ...sources];
  const linkedInLink = findLink(externalLinks, (descriptor) => descriptor.includes("linkedin.com"));
  const websiteLink = normalizeExternalUrl(
    officialWebsite ||
    getSourceUrl(findLink(officialLinks, (descriptor) => (
      !descriptor.includes("linkedin.com") &&
      !descriptor.includes("company-information.service.gov.uk") &&
      !descriptor.includes("companieshouse.gov.uk") &&
      !descriptor.includes("companies house") &&
      !descriptor.includes("company house")
    )))
  );
  const topLinks = [
    { label: "Website", url: websiteLink },
    { label: "LinkedIn", url: normalizeExternalUrl(getSourceUrl(linkedInLink)) },
  ].filter((link, index, links) => (
    hasMeaningfulValue(link.url) &&
    links.findIndex((candidate) => candidate.url === link.url) === index
  ));
  const sourceLinks = [
    ...officialLinks.filter((link) => hasMeaningfulValue(link?.url)),
    ...sources.filter((source) => hasMeaningfulValue(source?.url)),
  ];
  const compactSourceLinks = sourceLinks.slice(0, 5);
  const hiddenSourceCount = Math.max(sourceLinks.length - compactSourceLinks.length, 0);
  const confirmItems = Array.isArray(enrichment?.information_to_confirm)
    ? enrichment.information_to_confirm
    : [];
  const reviewItems = Array.isArray(enrichment?.recommended_human_review)
    ? enrichment.recommended_human_review
    : [];
  const enrichmentTabs = [
    {
      value: "signals",
      label: "Key signals",
      content: (
        <EnrichmentSection
          title="Key signals"
          items={enrichment?.key_signals || []}
          accent="#2563eb"
          emptyText="No key signals are available."
        />
      ),
    },
    {
      value: "people",
      label: "Key people",
      content: (
        <EnrichmentSection
          title="Key people"
          items={enrichment?.key_people || []}
          accent="#16a34a"
          emptyText="No key people are available."
          renderItem={getPersonTextValue}
        />
      ),
    },
    {
      value: "engagement",
      label: "Engagement angles",
      content: (
        <EnrichmentSection
          title="Engagement angles"
          items={enrichment?.engagement_angles || []}
          accent="#0891b2"
          emptyText="No engagement angles are available."
        />
      ),
    },
    {
      value: "watchouts",
      label: "Watchouts",
      content: (
        <EnrichmentSection
          title="Watchouts / caveats"
          items={enrichment?.watchouts || []}
          accent="#dc2626"
          emptyText="No watchouts are available."
        />
      ),
    },
    {
      value: "review",
      label: "Review",
      content: (
        <Box
          sx={{
            display: "grid",
            gap: 1.1,
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(2, minmax(0, 1fr))",
            },
          }}
        >
          <EnrichmentSection
            title="Information to confirm"
            items={confirmItems}
            accent="#d97706"
            emptyText="No confirmation items are available."
          />
          <EnrichmentSection
            title="Recommended review"
            items={reviewItems}
            accent="#475569"
            emptyText="No recommended review items are available."
          />
        </Box>
      ),
    },
  ];

  return (
    <Box
      sx={{
        border: "1px solid rgba(15, 23, 42, 0.09)",
        borderRadius: 2,
        backgroundColor: "#f8fafc",
        p: 1.5,
        display: "grid",
        gap: 1.25,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
        <Box>
          <Typography sx={{ fontWeight: 800, color: "#0f172a", fontSize: "1rem" }}>
            Enriched Data
          </Typography>
          {enrichment && (
            <Typography sx={{ color: "#64748b", fontSize: "0.84rem" }}>
              Vektor42 company context and engagement cues
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center" }}>
          {hasMeaningfulValue(status) && <Chip label={status} size="small" />}
          {topLinks.map((link) => (
            <Button
              key={`${link.label}-${link.url}`}
              variant="outlined"
              size="small"
              startIcon={<LaunchOutlinedIcon fontSize="small" />}
              href={link.url}
              target="_blank"
              rel="noreferrer"
              onClick={(event) => event.stopPropagation()}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: 999 }}
            >
              {link.label}
            </Button>
          ))}
          {hasMeaningfulValue(generatedAt) && <Chip label={`Enriched ${generatedAt}`} size="small" />}
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#64748b" }}>
          <CircularProgress size={18} />
          <Typography sx={{ fontSize: "0.9rem" }}>Loading enrichment...</Typography>
        </Box>
      ) : error ? (
        <Typography sx={{ color: "#b91c1c", fontSize: "0.9rem", fontWeight: 700 }}>
          {error}
        </Typography>
      ) : !enrichment ? (
        <Typography sx={{ color: "#64748b", fontSize: "0.9rem" }}>
          No enrichment is available for this company yet.
        </Typography>
      ) : (
        <>
          <Box sx={{ display: "grid", gap: 1 }}>
            <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center" }}>
              {hasMeaningfulValue(confidence) && (
                <Chip label={`Confidence: ${confidence}`} size="small" />
              )}
            </Box>

            <Box>
              {hasMeaningfulValue(whyItMatters) && (
                <>
                  <Typography sx={{ color: "#0f766e", fontWeight: 800, fontSize: "0.92rem", mb: 0.35 }}>
                    Why it matters
                  </Typography>
                  <Typography sx={{ color: "#1f2937", fontSize: "0.92rem", lineHeight: 1.45 }}>
                    {whyItMatters}
                  </Typography>
                </>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              border: "1px solid rgba(15, 23, 42, 0.08)",
              borderRadius: 2,
              backgroundColor: "#ffffff",
              overflow: "hidden",
            }}
          >
            <Tabs
              value={activeEnrichmentTab}
              onChange={(_, nextValue) => setActiveEnrichmentTab(nextValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                px: 1,
                pt: 0.75,
                borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: "0.82rem",
                  minHeight: 40,
                },
                "& .Mui-selected": {
                  color: "#0f766e",
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#0f766e",
                },
              }}
            >
              {enrichmentTabs.map((tab) => (
                <Tab key={tab.value} value={tab.value} label={tab.label} />
              ))}
            </Tabs>
            <Box sx={{ p: 1.25 }}>
              {enrichmentTabs.find((tab) => tab.value === activeEnrichmentTab)?.content}
            </Box>
          </Box>

          {sourceLinks.length > 0 && (
            <Box sx={{ display: "grid", gap: 0.75 }}>
              <Box>
                <Button
                  variant="text"
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    setSourcesExpanded((current) => {
                      if (current) {
                        setSourcesListMode(false);
                      }
                      return !current;
                    });
                  }}
                  sx={{ textTransform: "none", fontWeight: 800, px: 0.5, minWidth: 0 }}
                >
                  {sourcesExpanded ? "Hide sources" : `Sources (${sourceLinks.length})`}
                </Button>
              </Box>
              <Collapse in={sourcesExpanded} timeout={200} unmountOnExit>
                {sourcesListMode ? (
                  <Box
                    sx={{
                      border: "1px solid rgba(15, 23, 42, 0.08)",
                      borderRadius: 2,
                      backgroundColor: "#ffffff",
                      maxHeight: 210,
                      overflowY: "auto",
                      p: 1,
                    }}
                  >
                    <Box component="ul" sx={{ m: 0, pl: 2.1, display: "grid", gap: 0.8 }}>
                      {sourceLinks.map((source, index) => (
                        <Typography
                          component="li"
                          key={`${source.url}-${index}`}
                          sx={{ color: "#334155", fontSize: "0.86rem", lineHeight: 1.35 }}
                        >
                          <Button
                            variant="text"
                            size="small"
                            href={source.url}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(event) => event.stopPropagation()}
                            sx={{
                              textTransform: "none",
                              fontWeight: 700,
                              color: "#334155",
                              p: 0,
                              minWidth: 0,
                              verticalAlign: "baseline",
                              justifyContent: "flex-start",
                              textAlign: "left",
                            }}
                          >
                            {source.label || sourceLabel(source) || `Source ${index + 1}`}
                          </Button>
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center" }}>
                    {compactSourceLinks.map((source, index) => (
                      <Button
                        key={`${source.url}-${index}`}
                        variant="text"
                        size="small"
                        href={source.url}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(event) => event.stopPropagation()}
                        sx={{
                          textTransform: "none",
                          fontWeight: 700,
                          color: "#334155",
                          px: 0.5,
                          minWidth: 0,
                        }}
                      >
                        {source.label || sourceLabel(source) || "Source"}
                      </Button>
                    ))}
                    {hiddenSourceCount > 0 && (
                      <Button
                        variant="text"
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          setSourcesListMode(true);
                        }}
                        sx={{ textTransform: "none", fontWeight: 800, px: 0.5, minWidth: 0 }}
                      >
                        +{hiddenSourceCount} more
                      </Button>
                    )}
                  </Box>
                )}
              </Collapse>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

function matchesSelectedFacetValue(recordValue, selectedValues) {
  if (!selectedValues || selectedValues.length === 0) {
    return true;
  }

  if (!hasMeaningfulValue(recordValue)) {
    return false;
  }

  return selectedValues.includes(String(recordValue).trim());
}

function PeopleRecordPanel({ record, recordType, company }) {
  const isOfficer = recordType === "officers";
  const primaryLabel = isOfficer
    ? formatValue(record.role)
    : formatCompanyHouseFacetValue("pscKind", record.normalizedKind || record.kind);
  const secondaryDateLabel = isOfficer ? "Appointed On" : "Notified On";
  const secondaryDateValue = isOfficer ? record.appointedOn : record.notifiedOn;
  const tertiaryDateLabel = isOfficer ? "Resigned On" : "Ceased On";
  const tertiaryDateValue = isOfficer ? record.resignedOn : record.ceasedOn;

  return (
    <Box
      sx={{
        p: 1.25,
        border: "1px solid rgba(15, 23, 42, 0.08)",
        borderRadius: 2,
        backgroundColor: "#ffffff",
        display: "grid",
        gap: 0.85,
      }}
    >
      <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center" }}>
        <Typography sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1rem" }}>
          {record.name || "Unknown"}
        </Typography>
        <Chip
          label={isOfficer ? "Officer" : "PSC"}
          size="small"
          sx={{
            color: isOfficer ? "#0f766e" : "#1d4ed8",
            backgroundColor: isOfficer ? "#ecfeff" : "#eff6ff",
            border: `1px solid ${isOfficer ? "rgba(13, 148, 136, 0.35)" : "rgba(37, 99, 235, 0.28)"}`,
          }}
        />
      </Box>

      <DetailTextRow label={isOfficer ? "Role" : "Kind"} value={primaryLabel} />
      <DetailTextRow label="Status" value={record.status} />
      <DetailTextRow label={secondaryDateLabel} value={secondaryDateValue} />
      <DetailTextRow label={tertiaryDateLabel} value={tertiaryDateValue} />
      <DetailTextRow label="Date of Birth" value={record.dateOfBirth} />
      <DetailTextRow label="Nationality" value={record.nationality} />
      <DetailTextRow label="Country of Residence" value={record.countryOfResidence} />
      <DetailTextRow label="Correspondence Address" value={record.correspondenceAddress} />
      {!isOfficer && (
        <DetailTextRow label="Nature of Control" value={record.natureOfControl} />
      )}
      {isOfficer ? (
        <DetailTextRow
          label="Identity Verification Due"
          value={record.identityVerificationDue}
        />
      ) : (
        <>
          <DetailTextRow
            label="Identity Verification Due From"
            value={record.identityVerificationDueFrom}
          />
          <DetailTextRow
            label="Identity Verification Due By"
            value={record.identityVerificationDueBy}
          />
        </>
      )}
      <DetailTextRow
        label="Identity Verification Status"
        value={record.identityVerificationStatus}
      />

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 0.25 }}>
        {hasMeaningfulValue(company.sourceUrl) && (
          <Button
            variant="outlined"
            startIcon={<LaunchOutlinedIcon fontSize="small" />}
            href={company.sourceUrl}
            target="_blank"
            rel="noreferrer"
            onClick={(event) => event.stopPropagation()}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 999 }}
          >
            Open Company
          </Button>
        )}
        {isOfficer && hasMeaningfulValue(record.appointmentUrl) && (
          <Button
            variant="outlined"
            startIcon={<LaunchOutlinedIcon fontSize="small" />}
            href={record.appointmentUrl}
            target="_blank"
            rel="noreferrer"
            onClick={(event) => event.stopPropagation()}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 999 }}
          >
            Open Appointment
          </Button>
        )}
      </Box>
    </Box>
  );
}

export default function CompanyHouseCompanyCard({
  company,
  getApiJson,
  postJson,
  deleteJson,
  onOpenCrunchbaseMatch,
  onOpenGlobaldataMatch,
  selectedOfficerStatuses = [],
  selectedOfficerRoles = [],
  selectedPscStatuses = [],
  assignment,
  canManageAssignments = false,
  assignableUsers = [],
  assignableUsersLoading = false,
  onAssignmentsChanged,
  onContactedChanged,
}) {
  const [expanded, setExpanded] = useState(false);
  const [activeDetailsTab, setActiveDetailsTab] = useState("company");
  const [contactedLoading, setContactedLoading] = useState(false);
  const [contactedError, setContactedError] = useState("");
  const encodedCompanyName = useMemo(
    () => encodeURIComponent(company.companyName),
    [company.companyName]
  );
  const isContacted = Boolean(company.contacted);

  const sicSummary = useMemo(() => {
    if (!Array.isArray(company.sicCodes) || company.sicCodes.length === 0) {
      return "Unknown";
    }

    return company.sicCodes.join(", ");
  }, [company.sicCodes]);

  const handleCardToggle = () => {
    setExpanded((current) => !current);
  };

  const toggleContacted = async (event) => {
    event?.stopPropagation?.();
    if (!postJson || !deleteJson || contactedLoading) {
      return;
    }

    setContactedLoading(true);
    setContactedError("");

    try {
      if (isContacted) {
        await deleteJson(
          `/api/leads/lead-context/${LEAD_CONTEXTS.COMPANIES_HOUSE}/company/${encodedCompanyName}/contacted`
        );
      } else {
        await postJson(
          `/api/leads/lead-context/${LEAD_CONTEXTS.COMPANIES_HOUSE}/company/${encodedCompanyName}/contacted`
        );
      }

      onContactedChanged?.(company.companyName, !isContacted);
    } catch (error) {
      console.error("Unable to update contacted status:", error);
      setContactedError(error.message || "Unable to update contacted status.");
    } finally {
      setContactedLoading(false);
    }
  };

  const visibleOfficers = useMemo(
    () =>
      (company.officers || []).filter(
        (record) =>
          matchesSelectedFacetValue(record?.status, selectedOfficerStatuses) &&
          matchesSelectedFacetValue(record?.role, selectedOfficerRoles)
      ),
    [company.officers, selectedOfficerRoles, selectedOfficerStatuses]
  );

  const visiblePscItems = useMemo(
    () =>
      (company.pscItems || []).filter(
        (record) => matchesSelectedFacetValue(record?.status, selectedPscStatuses) && !record?.statement
      ),
    [company.pscItems, selectedPscStatuses]
  );

  const officerFiltersActive =
    selectedOfficerStatuses.length > 0 || selectedOfficerRoles.length > 0;
  const pscFiltersActive = selectedPscStatuses.length > 0;

  const officerFilterSummary = [
    ...selectedOfficerStatuses.map((value) =>
      `status ${formatCompanyHouseFacetValue("officerStatus", value)}`
    ),
    ...selectedOfficerRoles.map((value) =>
      `role ${formatCompanyHouseFacetValue("officerRole", value)}`
    ),
  ].join(", ");

  const pscFilterSummary = [
    ...selectedPscStatuses.map((value) =>
      `status ${formatCompanyHouseFacetValue("pscStatus", value)}`
    ),
  ].join(", ");

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 2,
        borderRadius: 3,
        borderLeft: "6px solid #0f766e",
        transition: "all 0.25s ease",
        boxShadow: expanded
          ? "0px 4px 14px rgba(0,0,0,0.18)"
          : "0px 2px 6px rgba(0,0,0,0.1)",
        ":hover": {
          boxShadow: "0px 6px 18px rgba(0,0,0,0.2)",
          transform: "scale(1.01)",
        },
      }}
    >
      <CardActionArea onClick={handleCardToggle}>
        <CardContent sx={{ position: "relative" }}>
          <Box
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              justifyContent: "flex-end",
              maxWidth: "70%",
            }}
          >
            <Chip
              label="Company House"
              size="small"
              sx={{
                color: "#0f766e",
                backgroundColor: "#ecfeff",
                border: "1px solid rgba(13, 148, 136, 0.35)",
              }}
            />
            {company.matchedToCrunchbase && (
              <Chip
                label="Crunchbase"
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  onOpenCrunchbaseMatch?.(company.primaryCrunchbaseMatchName || company.companyName);
                }}
                sx={{
                  color: "#166534",
                  backgroundColor: "#f0fdf4",
                  border: "1px solid rgba(34, 197, 94, 0.35)",
                  cursor: "pointer",
                }}
              />
            )}
            {company.matchedToGlobaldata && (
              <Chip
                label="GlobalData"
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  onOpenGlobaldataMatch?.(company.primaryGlobaldataMatchName || company.companyName);
                }}
                sx={{
                  color: "#075985",
                  backgroundColor: "#f0f9ff",
                  border: "1px solid rgba(14, 165, 233, 0.28)",
                  cursor: "pointer",
                }}
              />
            )}
            {isContacted && (
              <Chip
                label="Contacted"
                size="small"
                sx={{
                  color: "#9f1239",
                  backgroundColor: "#fff1f2",
                  border: "1px solid rgba(244, 63, 94, 0.28)",
                }}
              />
            )}
            {(company.matchedToClient ||
              company.clientLifecycleStatus === "EXISTING_CLIENT" ||
              company.clientLifecycleStatus === "CONVERTED_CLIENT" ||
              company.clientLifecycleStatus === "NOT_CLIENT_ANYMORE") && (
              <Chip
                label={
                  company.clientLifecycleStatus === "CONVERTED_CLIENT"
                    ? "Converted +"
                    : company.clientLifecycleStatus === "NOT_CLIENT_ANYMORE"
                    ? "Not client anymore"
                    : "Existing Client"
                }
                size="small"
                sx={{
                  color:
                    company.clientLifecycleStatus === "NOT_CLIENT_ANYMORE"
                      ? "#991b1b"
                      : company.clientLifecycleStatus === "CONVERTED_CLIENT"
                      ? "#075985"
                      : "#9a3412",
                  backgroundColor:
                    company.clientLifecycleStatus === "NOT_CLIENT_ANYMORE"
                      ? "#fef2f2"
                      : company.clientLifecycleStatus === "CONVERTED_CLIENT"
                      ? "#f0f9ff"
                      : "#fff7ed",
                  border:
                    company.clientLifecycleStatus === "NOT_CLIENT_ANYMORE"
                      ? "1px solid rgba(239, 68, 68, 0.35)"
                      : company.clientLifecycleStatus === "CONVERTED_CLIENT"
                    ? "1px solid rgba(14, 165, 233, 0.28)"
                    : "1px solid rgba(249, 115, 22, 0.35)",
                }}
              />
            )}
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 700, pr: 18 }}>
            {company.companyName}
          </Typography>

          <Typography
            variant="body1"
            sx={{
              mt: 1,
              fontStyle: "italic",
              fontSize: "1rem",
            }}
          >
            {formatCompanyHouseStatus(formatValue(company.companyStatus))} |{" "}
            {formatCompanyHouseCompanyType(formatValue(company.companyType))}
          </Typography>

          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 1.25 }}>
            <Chip label={`No. ${formatValue(company.companyNumber)}`} size="small" />
            <Chip label={`Officers: ${company.officerCount || 0}`} size="small" />
            <Chip label={`PSCs: ${company.pscCount || 0}`} size="small" />
          </Box>
        </CardContent>
      </CardActionArea>

      <Collapse in={expanded} timeout={{ enter: 400, exit: 250 }} unmountOnExit>
        <CardContent sx={{ backgroundColor: "#fafafa" }}>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              mt: 0.25,
              pt: 1.5,
              borderTop: "1px solid rgba(15, 23, 42, 0.08)",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: {
                  xs: "1fr",
                  lg: "minmax(0, 1.55fr) minmax(320px, 0.95fr)",
                },
                alignItems: "start",
              }}
            >
              <Box
                sx={{
                  border: "1px solid rgba(15, 23, 42, 0.08)",
                  borderRadius: 2,
                  backgroundColor: "#ffffff",
                  overflow: "hidden",
                }}
              >
                <Tabs
                  value={activeDetailsTab}
                  onChange={(_, nextValue) => setActiveDetailsTab(nextValue)}
                  variant="fullWidth"
                  sx={{
                    px: 1,
                    pt: 1,
                    borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
                    "& .MuiTab-root": {
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "0.84rem",
                      minHeight: 42,
                    },
                    "& .Mui-selected": {
                      color: "#0f766e",
                    },
                    "& .MuiTabs-indicator": {
                      backgroundColor: "#0f766e",
                    },
                  }}
                >
                  <Tab value="company" label="Company" />
                  <Tab value="officers" label="Officers" />
                  <Tab value="psc" label="Persons of Significant Control" />
                </Tabs>

                <Box sx={{ p: 1.25, display: "grid", gap: 1 }}>
                  {activeDetailsTab === "company" ? (
                    <>
                      <DetailTextRow label="Company Number" value={formatValue(company.companyNumber)} />
                      <DetailTextRow
                        label="Company Status"
                        value={formatCompanyHouseStatus(formatValue(company.companyStatus))}
                      />
                      <DetailTextRow
                        label="Company Type"
                        value={formatCompanyHouseCompanyType(formatValue(company.companyType))}
                      />
                      <DetailTextRow label="Incorporated On" value={formatValue(company.incorporatedOn)} />
                      <DetailTextRow
                        label="Registered Office Address"
                        value={formatValue(company.registeredOfficeAddress)}
                      />
                      <DetailTextRow label="SIC Codes" value={sicSummary} />
                      <DetailTextRow label="Officer Count" value={company.officerCount || 0} />
                      <DetailTextRow label="PSC Count" value={company.pscCount || 0} />
                      {company.matchedToCrunchbase && (
                        <DetailTextRow
                          label="Crunchbase Match"
                          value={formatValue(company.primaryCrunchbaseMatchName)}
                        />
                      )}
                      {company.matchedToGlobaldata && (
                        <DetailTextRow
                          label="GlobalData Match"
                          value={formatValue(company.primaryGlobaldataMatchName)}
                        />
                      )}
                      {company.matchedToClient && (
                        <DetailTextRow
                          label="Client Match"
                          value={formatValue(company.primaryClientMatchName)}
                        />
                      )}

                      {hasMeaningfulValue(company.sourceUrl) && (
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
                          <Button
                            variant="outlined"
                            startIcon={<LaunchOutlinedIcon fontSize="small" />}
                            href={company.sourceUrl}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(event) => event.stopPropagation()}
                            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 999 }}
                          >
                            Open Source
                          </Button>
                        </Box>
                      )}
                    </>
                  ) : activeDetailsTab === "officers" ? (
                    (company.officers || []).length === 0 ? (
                      <Typography sx={{ color: "#64748b", fontSize: "0.92rem" }}>
                        No officer records are available for this company.
                      </Typography>
                    ) : visibleOfficers.length === 0 ? (
                      <Typography sx={{ color: "#64748b", fontSize: "0.92rem" }}>
                        {officerFiltersActive
                          ? `No officers match the current filters${officerFilterSummary ? `: ${officerFilterSummary}.` : "."}`
                          : "No officer records are available for this company."}
                      </Typography>
                    ) : (
                      visibleOfficers.map((record, index) => (
                        <PeopleRecordPanel
                          key={`${company.companyNumber || company.companyName}-officer-${index}`}
                          record={record}
                          recordType="officers"
                          company={company}
                        />
                      ))
                    )
                  ) : (
                    (company.pscItems || []).length === 0 ? (
                      <Typography sx={{ color: "#64748b", fontSize: "0.92rem" }}>
                        No persons of significant control records are available for this company.
                      </Typography>
                    ) : visiblePscItems.length === 0 ? (
                      <Typography sx={{ color: "#64748b", fontSize: "0.92rem" }}>
                        {pscFiltersActive
                          ? `No persons of significant control records match the current filters${pscFilterSummary ? `: ${pscFilterSummary}.` : "."}`
                          : "No persons of significant control records are available for this company."}
                      </Typography>
                    ) : (
                      visiblePscItems.map((record, index) => (
                        <PeopleRecordPanel
                          key={`${company.companyNumber || company.companyName}-psc-${index}`}
                          record={record}
                          recordType="psc"
                          company={company}
                        />
                      ))
                    )
                  )}
                </Box>
              </Box>

              <CommentsPanel
                expanded={expanded}
                leadContext={LEAD_CONTEXTS.COMPANIES_HOUSE}
                organizationName={company.companyName}
                getApiJson={getApiJson}
                postJson={postJson}
              />
            </Box>

            <CompanyHouseEnrichmentPanel
              companyNumber={company.companyNumber}
              expanded={expanded}
              getApiJson={getApiJson}
            />

            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "start" }}>
              <LeadAssignmentPanel
                companyName={company.companyName}
                assignment={assignment}
                canManageAssignments={canManageAssignments}
                assignableUsers={assignableUsers}
                assignableUsersLoading={assignableUsersLoading}
                postJson={postJson}
                deleteJson={deleteJson}
                onAssignmentsChanged={onAssignmentsChanged}
              />
              <ContactedStatusControl
                contacted={isContacted}
                loading={contactedLoading}
                error={contactedError}
                onToggle={toggleContacted}
                sx={{ ml: { sm: 1 } }}
              />
            </Box>
          </Box>
        </CardContent>
      </Collapse>
    </Card>
  );
}
